import React, { createContext, useContext, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'theme-preference';

/** App is light mode only. Dark mode is disabled. */
export type ThemePreference = 'light' | 'dark' | 'default';

type ThemeContextType = {
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
  resolved: 'light' | 'dark';
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** Always light. Any stored dark/default is cleared so old sessions don't affect anything. */
function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'default') {
    localStorage.removeItem(STORAGE_KEY);
    return 'light';
  }
  return 'light';
}

/** Force light mode only: never add .dark. Do not set theme-color here — it fights route-level status bar (e.g. workout detail). */
function applyTheme(_preference: ThemePreference): 'light' {
  const root = document.documentElement;
  root.classList.remove('dark');
  return 'light';
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Run once to clear any stale dark/default from localStorage
  getStoredPreference();

  const setPreference = useCallback((_pref: ThemePreference) => {
    // No-op: app is light-only. Kept for API compatibility if something still calls useTheme().
    localStorage.setItem(STORAGE_KEY, 'light');
  }, []);

  // Ensure document never has .dark (light mode only).
  useEffect(() => {
    applyTheme('light');
  }, []);

  const value: ThemeContextType = { preference: 'light', setPreference, resolved: 'light' };
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
